<script>
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    ConversationCard,
    Spinner,
  },
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    conversationId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isMerging: false,
      showMergeConfirmModal: false,
      selectedConversationToMerge: null,
    };
  },
  computed: {
    conversations() {
      return this.$store.getters['contactConversations/getContactConversation'](
        this.contactId
      );
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    previousConversations() {
      return this.conversations.filter(
        conversation => conversation.id !== Number(this.conversationId)
      );
    },
    ...mapGetters({
      uiFlags: 'contactConversations/getUIFlags',
    }),
  },
  watch: {
    contactId(newContactId, prevContactId) {
      if (newContactId && newContactId !== prevContactId) {
        this.$store.dispatch('contactConversations/get', newContactId);
      }
    },
  },
  mounted() {
    this.$store.dispatch('contactConversations/get', this.contactId);
  },
  methods: {
    initiateMerge(conversation) {
      if (this.contact?.availability_status === 'online') {
        useAlert(
          this.$t('MERGE_CONVERSATION.ONLINE_STATUS', {
            contactName: this.contact.name || '',
          })
        );
        return;
      }
      this.selectedConversationToMerge = conversation;
      this.showMergeConfirmModal = true;
    },
    closeMergeConfirmModal() {
      this.showMergeConfirmModal = false;
      this.selectedConversationToMerge = null;
    },
    async confirmMerge() {
      if (!this.selectedConversationToMerge) return;

      try {
        this.isMerging = true;
        await ConversationApi.mergeConversations({
          conversationId: this.conversationId,
          mergeWithId: this.selectedConversationToMerge.id,
        });

        // 1. 获取当前活跃对话实例，使用 Mutation 清空本地消息缓存，强制触发从后端重新拉取合并后的全量消息
        const currentChat = this.$store.getters.getSelectedChat;
        if (currentChat) {
          this.$store.commit('SET_MISSING_MESSAGES', {
            id: currentChat.id,
            data: [],
          });
          currentChat.dataFetched = undefined;
          await this.$store.dispatch('setActiveChat', { data: currentChat });
        }

        // 2. 从左侧对话列表中移除被销毁的历史对话框，避免界面上显示两个对话框
        this.$store.commit('DELETE_CONVERSATION', this.selectedConversationToMerge.id);

        // 3. 重新加载右侧联系人的历史对话列表
        await this.$store.dispatch('contactConversations/get', this.contactId);

        useAlert(this.$t('MERGE_CONVERSATION.SUCCESS_MESSAGE'));
        this.closeMergeConfirmModal();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          this.$t('MERGE_CONVERSATION.ERROR_MESSAGE');
        useAlert(errorMessage);
      } finally {
        this.isMerging = false;
      }
    },
  },
};
</script>

<template>
  <div class="contact-conversation--panel">
    <div v-if="!uiFlags.isFetching" class="contact-conversation__wrap">
      <div
        v-if="!previousConversations.length"
        class="no-label-message px-4 p-3"
      >
        <span>
          {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
        </span>
      </div>
      <div v-else class="contact-conversation--list">
        <div
          v-for="conversation in previousConversations"
          :key="conversation.id"
          class="relative group"
        >
          <ConversationCard
            :chat="conversation"
            :hide-inbox-name="false"
            hide-thumbnail
            class="compact"
          />
          <button
            v-if="!isMerging"
            class="absolute bottom-2 right-2 px-2 py-1 bg-woot-500 hover:bg-woot-600 text-white rounded text-xxs font-medium cursor-pointer shadow-sm hidden group-hover:block transition-all"
            @click="initiateMerge(conversation)"
          >
            {{ $t('MERGE_CONVERSATION.MERGE_BUTTON') }}
          </button>
          <div
            v-else
            class="absolute bottom-2 right-2 px-2 py-1 bg-slate-200 text-slate-500 rounded text-xxs font-medium hidden group-hover:block"
          >
            {{ $t('MERGE_CONVERSATION.MERGING') }}
          </div>
        </div>
      </div>
    </div>
    <Spinner v-else />

    <woot-delete-modal
      v-if="showMergeConfirmModal"
      v-model:show="showMergeConfirmModal"
      :on-close="closeMergeConfirmModal"
      :on-confirm="confirmMerge"
      :title="$t('MERGE_CONVERSATION.TITLE')"
      :message="$t('MERGE_CONVERSATION.CONFIRM_MESSAGE', { sourceConversationId: selectedConversationToMerge ? `#${selectedConversationToMerge.id}` : '' })"
      :confirm-text="$t('MERGE_CONVERSATION.CONFIRM')"
      :reject-text="$t('MERGE_CONVERSATION.CANCEL')"
      :has-confirm-loading="isMerging"
    />
  </div>
</template>

<style lang="scss" scoped>
.no-label-message {
  @apply text-slate-500 dark:text-slate-400 mb-4;
}

:deep(.conversation) {
  @apply pr-0;
  .conversation--details {
    @apply pl-2;
  }
}
</style>
